import React, { useEffect } from 'react';
import { useParams, Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isSupportedLang, DEFAULT_LANG } from '../config/funnelByLang';
import { syncLanguageToProfile } from '../lib/languageSync';

/**
 * LangGuard — wrapper per le route del Funnel per lingua.
 *
 * ★ 2026-08-27 (Phase 57.C) — Valida il segmento `:lang` dell'URL:
 *   • se non supportato → redirect a `/` (splash)
 *   • se valido → sincronizza `i18n.language` con il segmento URL
 *     (così le pagine figlie renderizzano nella lingua giusta)
 *   • persiste la scelta lingua nel localStorage/cookie (best-effort)
 *
 * Le pagine effettive vengono rese dall'`<Outlet />` di react-router-v7.
 */
export default function LangGuard() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!isSupportedLang(lang)) return;
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
      try { localStorage.setItem('gp_locale', lang); } catch { /* ignore */ }
      try {
        document.cookie = `gp_locale=${lang}; path=/; max-age=${365 * 86400}; SameSite=Lax`;
        document.cookie = `gp_lang_chosen=${lang}; path=/; max-age=${365 * 86400}; SameSite=Lax`;
      } catch { /* ignore */ }
      // Persist to backend if the user is logged in (fire-and-forget).
      try { syncLanguageToProfile(lang); } catch { /* not logged in */ }
    }
  }, [lang, i18n]);

  if (!isSupportedLang(lang)) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
